/*
 * <seatfit-reviews>: boots the reviews section once the SeatFit core is ready.
 * Flow: index -> (total 0: stay hidden) -> summary -> first N cards for the vehicle in the URL ->
 * "View [n] More" adds the next batch. A 'seatfit:vehicle' event (picker changed the car without
 * a reload) re-orders the list from the top. Announces 'seatfit:reviews' {total, visible} for other
 * listeners; sticky tabs + jump buttons need none, their ResizeObserver sees the wrapper change.
 * Needs seatfit-reviews-data-loader.js, -card-render.js, -summary-render.js, -photo-lightbox.js first.
 */
(function () {
  'use strict';
  if (customElements.get('seatfit-reviews')) return;

  // The core scripts are deferred before this one, so 'seatfit:ready' has usually fired already.
  // Reviews do not need the vehicle rules: if the core never arrives, show them without a vehicle.
  function whenCore(cb) {
    if (window.SeatFit && window.SeatFit.stepsFor) return cb(window.SeatFit);
    var done = false;
    function go() { if (!done) { done = true; cb(window.SeatFit || null); } }
    document.addEventListener('seatfit:ready', go, { once: true });
    if (document.readyState === 'complete') setTimeout(go, 0);
    else window.addEventListener('load', function () { setTimeout(go, 0); }, { once: true });
  }

  function vehicleFromUrl(SF) {
    try {
      var p = SF && SF.readParams ? SF.readParams() : {};
      return { make: p.make || null, model: p.model || null };
    } catch (e) { console.warn('[SeatFit reviews] could not read the vehicle from the URL', e); }
    return { make: null, model: null };
  }

  function readLabels(root) {
    var el = root.querySelector('script[data-sf-rv-labels]');
    try { return JSON.parse((el && el.textContent) || '{}') || {}; } catch (e) { return {}; }
  }

  function intAttr(v, fallback) { return parseInt(v, 10) > 0 ? parseInt(v, 10) : fallback; }

  class SeatfitReviews extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;
      this._gen = this._gen || 0;
      this.labels = readLabels(this);
      this.opts = {
        first: intAttr(this.dataset.first, 5),
        step: intAttr(this.dataset.step, 8),
        maxPhotos: intAttr(this.dataset.maxPhotos, 5),
        showDist: this.dataset.showDist !== 'false',
        showScales: this.dataset.showScales !== 'false'
      };
      this.list = this.querySelector('[data-sf-rv-list]');
      this.moreBtn = this.querySelector('[data-sf-rv-more]');
      this.bind();
      var self = this;
      whenCore(function (SF) { self.boot(SF); });
    }

    disconnectedCallback() {
      document.removeEventListener('seatfit:vehicle', this._onVehicle);
      window.removeEventListener('resize', this._onResize);
      if (this.lightbox) this.lightbox.destroy();
      this.lightbox = null;
      this._started = false;
      this._gen = (this._gen || 0) + 1; // results still in flight belong to the old render
    }

    bind() {
      var self = this;
      this._onVehicle = function (e) {
        var c = e.detail && e.detail.ctx;
        if (!c) return;
        self.setVehicle({ make: (c.make && c.make.s) || null, model: (c.model && c.model.s) || null });
      };
      document.addEventListener('seatfit:vehicle', this._onVehicle);
      var timer = null;
      this._onResize = function () {
        clearTimeout(timer);
        timer = setTimeout(function () { self.refitReadMore(); }, 150);
      };
      window.addEventListener('resize', this._onResize);
      if (this._elBound) return; // element listeners survive a disconnect; only add them once
      this._elBound = true;
      if (this.moreBtn) this.moreBtn.addEventListener('click', function () { self.load(self.opts.step, self._gen); });
      if (this.list) this.list.addEventListener('click', function (e) { self.onListClick(e); });
    }

    onListClick(e) {
      var R = (window.SeatFitReviews || {}).render;
      var card = e.target.closest('.sf-rv-card');
      if (!card || !R) return;
      if (e.target.closest('[data-sf-rv-readmore]')) return R.toggleReadMore(card, this.labels);
      var pic = e.target.closest('[data-sf-rv-pic]');
      var LB = (window.SeatFitReviews || {}).Lightbox;
      if (!pic || !LB) return;
      if (!this.lightbox) this.lightbox = new LB();
      this.lightbox.open(card.sfPhotos || [], parseInt(pic.getAttribute('data-sf-rv-pic'), 10) || 0, card.sfCaption, pic);
    }

    boot(SF) {
      var self = this;
      var data = (window.SeatFitReviews || {}).data;
      if (!data || !this.list) return this.showEmpty('scripts missing');
      this.indexUrl = data.indexUrl(this.dataset.indexUrl); // the bank of the product this page sells
      data.loadIndex(this.indexUrl).then(function (index) {
        if (!index || !(Number(index.total) > 0)) return self.showEmpty('no reviews');
        self.index = index;
        self.setVisible(true);
        self.paintSummary();
        self.setVehicle(self.pendingVehicle || vehicleFromUrl(SF), true);
      }).catch(function (e) {
        console.warn('[SeatFit reviews] review index failed to load', e);
        self.showEmpty('load failed');
      });
    }

    paintSummary() {
      var summary = (window.SeatFitReviews || {}).summary;
      var box = this.querySelector('[data-sf-rv-summary]');
      if (!summary || !box) return;
      try {
        summary({
          summary: box,
          score: box.querySelector('[data-sf-rv-score]'),
          dist: box.querySelector('[data-sf-rv-dist]'),
          scales: box.querySelector('[data-sf-rv-scales]')
        }, this.index, this.labels, this.opts);
      } catch (e) { console.warn('[SeatFit reviews] summary failed', e); }
    }

    setVehicle(veh, force) {
      if (!this.index) { this.pendingVehicle = veh; return; }
      var key = (veh.make || '') + '/' + (veh.model || '');
      if (!force && key === this.vehKey) return;
      this.vehKey = key;
      var gen = ++this._gen;
      this.pool = window.SeatFitReviews.data.createPool(this.index, this.indexUrl, veh);
      this.list.textContent = '';
      this.load(this.opts.first, gen);
    }

    load(n, gen) {
      var self = this;
      var R = window.SeatFitReviews.render;
      if (!this.pool) return;
      if (this.moreBtn) this.moreBtn.disabled = true;
      this.pool.take(n).then(function (reviews) {
        if (gen !== self._gen) return; // the vehicle changed while this batch was loading
        reviews.forEach(function (rv) {
          try {
            var card = R.card(rv, self.labels, self.opts);
            self.list.appendChild(card);
            R.fitReadMore(card);
          } catch (e) { console.warn('[SeatFit reviews] skipped a review that failed to render', e); }
        });
        self.updateMore();
      }).catch(function (e) {
        console.warn('[SeatFit reviews] could not load more reviews', e);
        if (gen === self._gen) self.updateMore();
      });
    }

    updateMore() {
      if (!this.moreBtn || !this.pool) return;
      var n = Math.min(this.opts.step, this.pool.remaining());
      this.moreBtn.disabled = false;
      this.moreBtn.hidden = !(this.pool.hasMore() && n > 0);
      this.moreBtn.textContent = String(this.labels.more || 'View [n] More').replace(/\[n\]/g, String(n));
    }

    refitReadMore() {
      var R = (window.SeatFitReviews || {}).render;
      if (!R || !this.list) return;
      Array.prototype.forEach.call(this.list.querySelectorAll('.sf-rv-card'), function (c) { R.fitReadMore(c); });
    }

    // The Shopify wrapper is hidden too, so anything that checks the section anchor sees display:none.
    setVisible(on) {
      this.hidden = !on;
      var wrap = this.closest('.shopify-section');
      if (wrap) wrap.hidden = !on;
      document.dispatchEvent(new CustomEvent('seatfit:reviews', {
        detail: { total: this.index ? Number(this.index.total) || 0 : 0, visible: on }
      }));
    }

    // In the theme editor the section stays selectable and explains why the store hides it.
    showEmpty(reason) {
      var note = this.querySelector('[data-sf-rv-note]');
      if (this.hasAttribute('data-design-mode') && note) {
        note.hidden = false;
        if (reason !== 'no reviews') note.textContent = 'Theme editor only: reviews could not load (' + reason + ').';
        this.hidden = false;
        return;
      }
      this.setVisible(false);
    }
  }

  customElements.define('seatfit-reviews', SeatfitReviews);
})();
